import axios from 'axios'
import { useToasts } from 'hooks'
import Bugsnag from 'lib/bugsnag'
import { useCallback, useMemo, useState } from 'react'

export type TFileUploadStatus = {
  type: string
  name: string
  started: boolean
  progress: number
  error?: string
  isFileReady: boolean
  s3Key: string
  userRemoved?: boolean
}

export const getNewFileStatus = (file: File): TFileUploadStatus => {
  return {
    started: false,
    progress: 0,
    type: file.type,
    name: file.name,
    isFileReady: false,
    s3Key: ''
  }
}

export default function useS3FileUpload(sizeLimitMB?: number) {
  const [progress, setProgress] = useState(0)
  const [isFileReady, setIsFileReady] = useState(false)
  const [error, setError] = useState<string>()
  const [s3Key, setS3Key] = useState<string>('')
  const [file, setFile] = useState<File>()
  const { addToast } = useToasts()

  const upload = useCallback(
    async (file: File, uploadUrl: string, s3Key: string) => {
      const fileMb = file.size / 1024 ** 2
      if (sizeLimitMB && fileMb > sizeLimitMB) {
        setError('Over size limit')
        return
      }

      setIsFileReady(false)
      setFile(file)
      setS3Key(s3Key)
      setProgress(0)
      setError(undefined)
      try {
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type
          },
          onUploadProgress: function (progressEvent) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setProgress(progress)
          }
        })

        setIsFileReady(true)
        return s3Key
      } catch (error) {
        setIsFileReady(false)
        setError(error.message)
        Bugsnag.notify(error)
        addToast(`Image upload failed`, { appearance: 'warning' })
        return
      }
    },
    [addToast, sizeLimitMB]
  )

  const uploadStatus: TFileUploadStatus = useMemo(
    () => ({
      started: progress > 0,
      name: file?.name || '',
      progress,
      s3Key,
      isFileReady,
      type: file?.type || '',
      error
    }),
    [error, file, isFileReady, progress, s3Key]
  )

  return {
    upload,
    uploadStatus
  }
}
